// ============================================================
// PRESENTATION / SEED CONSTANTS — ported verbatim from mock/index.html.
// Color-coded meta maps for badges/pills, the 6 task phases, task types,
// and the seed/derivation helpers (legacyToPhase / inferTaskType / hashKey).
// TODAY is the demo "today" used across home/calendar/monthly.
// ============================================================
import type {
  CalendarKind,
  Health,
  NoteSource,
  TaskPhase,
  TaskTypeId,
  Trend,
} from '../types/models';

/** 実日付（JST）の「今日」。以前はデモ固定日 '2026-06-23' を使っていたが、
 *  ライブ運用で「今日」がズレるため実日付に変更。home/calendar/monthly が参照。 */
function todayYmdJst(): string {
  const now = new Date();
  // 端末のタイムゾーンに依存せず日本時間(UTC+9)の暦日を返す。
  const jst = new Date(now.getTime() + (now.getTimezoneOffset() + 540) * 60000);
  const p = (x: number) => String(x).padStart(2, '0');
  return jst.getFullYear() + '-' + p(jst.getMonth() + 1) + '-' + p(jst.getDate());
}
export const TODAY = todayYmdJst();

export const WEEKDAY_JP = ['日', '月', '火', '水', '木', '金', '土'] as const;

export interface ColorMeta {
  label: string;
  bg: string;
  fg: string;
}

export const SOURCE_META: Record<NoteSource, { label: string; icon: string; bg: string; fg: string }> = {
  meet: { label: 'Google Meet', icon: 'video', bg: '#E0F2FE', fg: '#0369A1' },
  notta: { label: 'Notta', icon: 'mic', bg: '#E0E7FF', fg: '#4338CA' },
  manual: { label: '手動アップロード', icon: 'upload', bg: '#F3F4F6', fg: '#4B5563' },
};

export const STATUS_META: Record<string, ColorMeta> = {
  done: { label: '完了', bg: '#D1FAE5', fg: '#047857' },
  progress: { label: '対応中', bg: '#FEF3C7', fg: '#B45309' },
  todo: { label: '未対応', bg: '#FEE2E2', fg: '#B91C1C' },
};

export const HEALTH_META: Record<Health, { label: string; bg: string; fg: string; dot: string }> = {
  good: { label: '順調', bg: '#D1FAE5', fg: '#047857', dot: '#10B981' },
  watch: { label: '注意', bg: '#FEF3C7', fg: '#B45309', dot: '#F59E0B' },
  risk: { label: '要対応', bg: '#FEE2E2', fg: '#B91C1C', dot: '#DC2626' },
};

export interface PhaseMeta {
  id: TaskPhase;
  label: string;
  order: number;
  bg: string;
  fg: string;
  dot: string;
  barColor: string;
  offFlow?: boolean;
}

// 6段階フェーズ（信号機回避：色相を増やさず明度/彩度の単調進行）
export const TASK_PHASES: PhaseMeta[] = [
  { id: 'todo', label: '未着手', order: 0, bg: '#F1F5F9', fg: '#475569', dot: '#94A3B8', barColor: '#94A3B8' },
  { id: 'doing', label: '進行中', order: 1, bg: '#E0F2FE', fg: '#0369A1', dot: '#0EA5E9', barColor: '#0EA5E9' },
  { id: 'review', label: '確認中', order: 2, bg: '#E0F2FE', fg: '#075985', dot: '#0369A1', barColor: '#0369A1' },
  { id: 'hold', label: '保留', order: 3, bg: '#FEF3C7', fg: '#B45309', dot: '#F59E0B', barColor: '#F59E0B', offFlow: true },
  { id: 'report', label: '完了報告', order: 4, bg: '#ECFDF5', fg: '#047857', dot: '#34D399', barColor: '#34D399' },
  { id: 'done', label: '完了', order: 5, bg: '#D1FAE5', fg: '#047857', dot: '#10B981', barColor: '#10B981' },
];
export const PHASE_MAP: Record<string, PhaseMeta> = Object.fromEntries(TASK_PHASES.map((p) => [p.id, p]));

export interface TaskTypeMeta {
  id: TaskTypeId;
  label: string;
  bg: string;
  fg: string;
}
export const TASK_TYPES: TaskTypeMeta[] = [
  { id: 'contact', label: '連絡', bg: '#E0F2FE', fg: '#0369A1' },
  { id: 'create', label: '作成', bg: '#E0E7FF', fg: '#4338CA' },
  { id: 'review', label: '確認', bg: '#D1FAE5', fg: '#047857' },
  { id: 'respond', label: '対応', bg: '#FEF3C7', fg: '#B45309' },
  { id: 'approve', label: '数値承認', bg: '#FEE2E2', fg: '#B91C1C' },
];
export const TASK_TYPE_MAP: Record<string, TaskTypeMeta> = Object.fromEntries(TASK_TYPES.map((t) => [t.id, t]));

export const CAL_KIND: Record<CalendarKind, { label: string; bg: string; fg: string; dot: string; icon: string }> = {
  meeting: { label: '会議', bg: '#E0F2FE', fg: '#0369A1', dot: '#0EA5E9', icon: 'video' },
  deadline: { label: 'タスク期限', bg: '#FEF3C7', fg: '#B45309', dot: '#F59E0B', icon: 'flag' },
  plan: { label: '予定', bg: '#F3F4F6', fg: '#374151', dot: '#475569', icon: 'calendar' },
};

export const TREND_META: Record<Trend, { icon: string; fg: string }> = {
  up: { icon: 'trending-up', fg: '#047857' },
  down: { icon: 'trending-down', fg: '#B45309' },
  flat: { icon: 'minus', fg: '#6B7280' },
};

export const LOAD_META: Record<string, { label: string; bg: string; fg: string; dot: string }> = {
  light: { label: '軽', bg: '#D1FAE5', fg: '#047857', dot: '#10B981' },
  normal: { label: '適正', bg: '#E0F2FE', fg: '#0369A1', dot: '#0EA5E9' },
  heavy: { label: '過多', bg: '#FEE2E2', fg: '#B91C1C', dot: '#DC2626' },
};

export const TOOL_CAT: Record<string, { bg: string; fg: string }> = {
  業務アプリ: { bg: '#FEF3C7', fg: '#B45309' },
  ドキュメント: { bg: '#E0F2FE', fg: '#0369A1' },
  コミュニケーション: { bg: '#E0E7FF', fg: '#4338CA' },
  サイト: { bg: '#D1FAE5', fg: '#047857' },
};

// ── seed / derivation helpers (used by mockAdapter to build the tasks single source) ──
export function hashKey(k: string): number {
  let h = 0;
  for (let i = 0; i < k.length; i++) {
    h = (h * 31 + k.charCodeAt(i)) >>> 0;
  }
  return h;
}

/** 既存3値(status: todo/progress/done) → 6フェーズへ決定論的に散らす。 */
export function legacyToPhase(status: string, taskKey: string): TaskPhase {
  const h = hashKey(taskKey || '');
  if (status === 'done') return 'done';
  if (status === 'progress') {
    const opt: TaskPhase[] = ['doing', 'review', 'report'];
    return opt[h % opt.length];
  }
  const opt: TaskPhase[] = ['todo', 'hold'];
  return opt[h % opt.length];
}

/** 議事録アクション本文から種別を推定（初期シード用・キーワードベース）。 */
export function inferTaskType(taskText: string): TaskTypeId {
  const s = taskText || '';
  if (/承認|数値|レビュー(?=.*数)|予算/.test(s)) return 'approve';
  if (/作成|ドラフト|整備|用意|作り|フォーマット|資料|提案書|リスト/.test(s)) return 'create';
  if (/確認|チェック|レビュー|見直し|観測/.test(s)) return 'review';
  if (/連絡|共有|送付|案内|報告/.test(s)) return 'contact';
  return 'respond';
}

export function nowIso(): string {
  return TODAY + 'T12:00:00+09:00';
}

const SEED_MONTHS = ['2026-04', '2026-05', '2026-06'];
/** 初期シードの「完了」タスクの completedAt を過去数ヶ月へ決定論的に散らす。 */
export function seedCompletedAt(taskKey: string): string {
  const h = hashKey(taskKey || '');
  const ym = SEED_MONTHS[h % SEED_MONTHS.length];
  const day = String((h % 26) + 2).padStart(2, '0');
  return ym + '-' + day + 'T12:00:00+09:00';
}
